import {combineLatest} from 'rxjs';
import {distinctUntilChanged, filter, map, startWith} from 'rxjs/operators';
import {DEFAULT_USD_PRICE_SCALE} from '@/const/data-config';
import {div, numUtil, toFixed} from '@/helper/num';
import {calcTotalAssets} from '@/assets/calc';
import {currenciesCurrencyCache$} from '@/config';
import {fiat$} from '@/state/fiat';
import {indexPricesCache$} from '@/price-index/price-index';
import {marginAccountsData$, marginAccountOverview$} from '../accounts/accounts';
import {marginTradeCrossRiskLevel$} from '../accounts/risk-level';
import type {Assets, PriceEntity, AssetEntity, MarginAssetsOverview} from '@/types';

export const marginAssets$ = marginAccountsData$.pipe(
  map(({accounts}) =>
    accounts.map((account) => <AssetEntity>{currency: account.currency, valueEv: account.netEquityEv})
  ),
  startWith([])
);

export const marginTotalAssets$ = combineLatest([
  marginAssets$,
  indexPricesCache$,
  currenciesCurrencyCache$,
  fiat$,
]).pipe(
  map(([marginAssets, indexPricesCache, currencies, fiat]) =>
    calcTotalAssets(marginAssets, indexPricesCache, currencies, fiat, true)
  ),
  filter<Assets>(Boolean)
);

const btcIndexPriceEv$ = indexPricesCache$.pipe(
  filter((o) => o.has('.BTC')),
  map((o): PriceEntity => o.get('.BTC')),
  map((o) => o.priceEp),
  distinctUntilChanged()
);

const btcCurrency$ = currenciesCurrencyCache$.pipe(
  map((currencies) => currencies.get('BTC')),
  filter(Boolean)
);
const usdCurrency$ = currenciesCurrencyCache$.pipe(
  map((currencies) => currencies.get('USD')),
  filter(Boolean)
);

export const marginAssetsOverview$ = combineLatest([
  marginAccountOverview$,
  marginTradeCrossRiskLevel$,
  btcIndexPriceEv$,
  btcCurrency$,
  usdCurrency$,
]).pipe(
  map(([account, marginTradeCrossRiskLevel, btcIndexPriceEv, btcCurrency, usdCurrency]): MarginAssetsOverview => {
    const {
      totalEquityEv,
      totalDebtEv,
      netEquityEv,
      maxTransferOutValueEv,
      totalAvailableEv,
      totalMarginEv,
      totalFreezeEv,
    } = account;
    const totalEquityBtcEv = convert(totalEquityEv, btcIndexPriceEv, btcCurrency);
    const totalDebtBtcEv = convert(totalDebtEv, btcIndexPriceEv, btcCurrency);
    const netEquityBtcEv = convert(netEquityEv, btcIndexPriceEv, btcCurrency);
    return {
      ...marginTradeCrossRiskLevel,
      totalEquityEv,
      totalEquityUsd: numUtil.delimit(numUtil.floor(totalEquityEv, usdCurrency.valuePrecision)),
      totalEquityBtc: numUtil.delimit(totalEquityBtcEv),
      totalDebtEv,
      totalDebtUsd: numUtil.delimit(toFixed(totalDebtEv, usdCurrency.valuePrecision, 3)),
      totalDebtBtc: numUtil.delimit(totalDebtBtcEv),
      netEquityEv,
      netEquityUsd: numUtil.delimit(numUtil.floor(netEquityEv, usdCurrency.valuePrecision)),
      netEquityBtc: numUtil.delimit(netEquityBtcEv),
      maxTransferOutValueEv,
      totalAvailableEv,
      totalAvailableUsd: numUtil.delimit(numUtil.floor(totalAvailableEv, usdCurrency.valuePrecision)),
      totalMarginEv,
      totalMarginUsd: numUtil.delimit(numUtil.floor(totalMarginEv, usdCurrency.valuePrecision)),
      totalFreezeEv,
      totalFreezeUsd: numUtil.delimit(numUtil.floor(totalFreezeEv, usdCurrency.valuePrecision)),
    };
  })
);

function convert(netEquityEv, btcIndexPriceEv, {valuePrecision}) {
  return numUtil.floor(div(netEquityEv, btcIndexPriceEv), valuePrecision, -DEFAULT_USD_PRICE_SCALE);
}
